import { BaseService, ServiceResponse, PaginatedResponse } from './base.service';

export interface Conversation {
  id: string;
  conversation_type: 'direct' | 'group';
  title?: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  participants?: ConversationParticipant[];
  last_message?: DirectMessage;
  unread_count?: number;
}

export interface ConversationParticipant {
  id: string;
  conversation_id: string;
  user_id: string;
  joined_at: string;
  left_at?: string;
  role: 'admin' | 'member';
  is_active: boolean;
  user?: UserProfile;
}

export interface DirectMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content?: string;
  message_type: 'text' | 'round_invite' | 'photo' | 'round_share' | 'location' | 'system';
  message_data: Record<string, unknown>;
  reply_to_id?: string;
  edited_at?: string;
  deleted_at?: string;
  created_at: string;
  sender?: UserProfile;
  reply_to?: DirectMessage;
  status?: MessageStatus[];
}

export interface MessageStatus {
  id: string;
  message_id: string;
  user_id: string;
  status: 'sent' | 'delivered' | 'read';
  timestamp: string;
}

export interface UserProfile {
  id: string;
  display_name?: string;
  avatar_url?: string;
  handicap?: number;
  club_id?: string;
}

export interface CreateConversationRequest {
  conversation_type: 'direct' | 'group';
  participant_ids: string[];
  title?: string;
  description?: string;
}

export interface SendMessageRequest {
  conversation_id: string;
  content?: string;
  message_type?: 'text' | 'round_invite' | 'photo' | 'round_share' | 'location' | 'system';
  message_data?: Record<string, unknown>;
  reply_to_id?: string;
}

export class MessagingService extends BaseService {
  private static instance: MessagingService;

  static getInstance(): MessagingService {
    if (!MessagingService.instance) {
      MessagingService.instance = new MessagingService();
    }
    return MessagingService.instance;
  }

  async getConversations(limit: number = 20, offset: number = 0): Promise<ServiceResponse<PaginatedResponse<Conversation>>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // First, get conversations where user is a participant
      const { data: userConversations, error: convError } = await this.supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (convError) {
        throw convError;
      }

      const conversationIds = userConversations?.map(uc => uc.conversation_id) || [];
      
      if (conversationIds.length === 0) {
        return {
          data: [],
          total: 0,
          page: 1,
          limit,
          hasNextPage: false
        };
      }

      // Get friend IDs for privacy-aware name display
      const { data: friends } = await this.supabase
        .from('friend_relationships')
        .select('requester_id, addressee_id')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
        .eq('status', 'accepted');

      const friendIds = (friends || []).map(f =>
        f.requester_id === user.id ? f.addressee_id : f.requester_id
      );

      // Get conversation details - handle the array properly (include nickname for privacy-aware display)
      const { data, error, count } = await this.supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            *,
            user:profiles(
              id,
              first_name,
              last_name,
              display_name,
              avatar_url,
              handicap,
              nickname
            )
          )
        `, { count: 'exact' })
        .in('id', conversationIds.filter(id => id != null)) // Filter out null/undefined values
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      // Get unique participant IDs for privacy settings lookup (show_real_name toggle only)
      const allParticipantIds = [...new Set(
        (data || []).flatMap(conv =>
          (conv.participants || []).map(p => p.user_id)
        )
      )];

      const { data: privacySettings } = await this.supabase
        .from('user_privacy_settings')
        .select('user_id, show_real_name')
        .in('user_id', allParticipantIds.length > 0 ? allParticipantIds : ['__no_match__']);

      const privacyMap = new Map(
        (privacySettings || []).map(ps => [ps.user_id, ps])
      );

      // Get last message for each conversation - with error handling for RLS
      const conversations = await Promise.all((data || []).map(async (conv) => {
        let lastMessage = null;
        try {
          const { data: messageData, error: messageError } = await this.supabase
            .from('direct_messages')
            .select(`
              id,
              content,
              message_type,
              created_at,
              sender:profiles(
                id,
                display_name,
                avatar_url
              )
            `)
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (!messageError) {
            lastMessage = messageData;
          } else {
            console.warn('Could not fetch last message for conversation:', conv.id, messageError);
          }
        } catch (error) {
          console.warn('Error fetching last message for conversation:', conv.id, error);
        }

        // Get unread count for this conversation using simple queries - with error handling
        let unreadCount = 0;
        try {
          const { data: convMessages, error: messagesError } = await this.supabase
            .from('direct_messages')
            .select('id')
            .eq('conversation_id', conv.id)
            .neq('sender_id', user.id);

          if (!messagesError && convMessages && convMessages.length > 0) {
            const { data: readMessages } = await this.supabase
              .from('message_status')
              .select('message_id')
              .eq('user_id', user.id)
              .eq('status', 'read')
              .in('message_id', convMessages.map(m => m.id));

            const readMessageIds = new Set(readMessages?.map(rm => rm.message_id) || []);
            unreadCount = convMessages.filter(message => !readMessageIds.has(message.id)).length;
          } else if (messagesError) {
            console.warn('Could not fetch messages for unread count:', conv.id, messagesError);
          }
        } catch (error) {
          console.warn('Error calculating unread count for conversation:', conv.id, error);
        }

        // Apply privacy-aware name display to participants
        const enrichedParticipants = (conv.participants || []).map(participant => {
          if (!participant.user) return participant;

          const participantId = participant.user_id;
          const isFriendWithParticipant = friendIds.includes(participantId) || participantId === user.id;

          const participantPrivacy = privacyMap.get(participantId);
          const showRealName = participantPrivacy?.show_real_name ?? true;
          // Nickname now comes from profiles.nickname (single source of truth)
          const privacyNickname = participant.user?.nickname;

          let displayName: string;
          if (isFriendWithParticipant || showRealName) {
            const firstName = participant.user?.first_name || '';
            const lastName = participant.user?.last_name || '';
            displayName = `${firstName} ${lastName}`.trim() || participant.user?.display_name || 'Unknown';
          } else {
            displayName = privacyNickname || participant.user?.display_name || 'Unknown';
          }

          return {
            ...participant,
            user: {
              ...participant.user,
              display_name: displayName
            }
          };
        });

        return {
          ...conv,
          participants: enrichedParticipants,
          last_message: lastMessage,
          unread_count: unreadCount
        };
      }));

      return {
        data: conversations,
        total: count || 0,
        page: Math.floor(offset / limit) + 1,
        limit,
        hasNextPage: (offset + limit) < (count || 0)
      };
    });
  }

  async createConversation(data: CreateConversationRequest): Promise<ServiceResponse<Conversation>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // For direct messages, check if conversation already exists
      if (data.conversation_type === 'direct' && data.participant_ids.length === 1) {
        const otherUserId = data.participant_ids[0];
        
        // Get all user's conversations first
        const { data: userConversations } = await this.supabase
          .from('conversation_participants')
          .select('conversation_id')
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (userConversations && userConversations.length > 0) {
          const conversationIds = userConversations.map(uc => uc.conversation_id).filter(id => id != null);
          
          if (conversationIds.length > 0) {
            // Check if any of these conversations are direct messages with the target user
            const { data: existingConv } = await this.supabase
              .from('conversations')
              .select('*')
              .in('id', conversationIds)
              .eq('conversation_type', 'direct');

            if (existingConv && existingConv.length > 0) {
              // Check each conversation to see if it involves the target user
              for (const conv of existingConv) {
                const { data: participants } = await this.supabase
                  .from('conversation_participants')
                  .select('user_id')
                  .eq('conversation_id', conv.id)
                  .eq('is_active', true);

                if (participants && participants.length === 2) {
                  const participantIds = participants.map(p => p.user_id);
                  if (participantIds.includes(otherUserId) && participantIds.includes(user.id)) {
                    // Return existing conversation
                    return await this.getConversation(conv.id);
                  }
                }
              }
            }
          }
        }
      }

      // Create new conversation
      const { data: conversation, error } = await this.supabase
        .from('conversations')
        .insert({
          conversation_type: data.conversation_type,
          title: data.title,
          description: data.description,
          created_by: user.id
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Add participants
      const participantInserts = [
        { conversation_id: conversation.id, user_id: user.id, role: 'admin' },
        ...data.participant_ids.map(userId => ({
          conversation_id: conversation.id,
          user_id: userId,
          role: 'member' as const
        }))
      ];

      const { error: participantsError } = await this.supabase
        .from('conversation_participants')
        .insert(participantInserts);

      if (participantsError) {
        throw participantsError;
      }

      return await this.getConversation(conversation.id);
    });
  }

  async getConversation(conversationId: string): Promise<ServiceResponse<Conversation>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // First check if user is a participant
      const { data: participation, error: participationError } = await this.supabase
        .from('conversation_participants')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (participationError || !participation) {
        throw new Error('Access denied to this conversation');
      }

      // Get friend IDs for privacy-aware name display
      const { data: friends } = await this.supabase
        .from('friend_relationships')
        .select('requester_id, addressee_id')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
        .eq('status', 'accepted');

      const friendIds = (friends || []).map(f =>
        f.requester_id === user.id ? f.addressee_id : f.requester_id
      );

      // Get conversation details
      const { data, error } = await this.supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (error) {
        throw error;
      }

      // Get participants separately with first_name, last_name, and nickname
      const { data: participants, error: participantsError } = await this.supabase
        .from('conversation_participants')
        .select(`
          *,
          user:profiles(
            id,
            first_name,
            last_name,
            display_name,
            avatar_url,
            handicap,
            nickname
          )
        `)
        .eq('conversation_id', conversationId)
        .eq('is_active', true);

      if (participantsError) {
        throw participantsError;
      }

      // Get privacy settings for all participants (show_real_name toggle only)
      const participantIds = (participants || []).map(p => p.user_id);
      const { data: privacySettings } = await this.supabase
        .from('user_privacy_settings')
        .select('user_id, show_real_name')
        .in('user_id', participantIds.length > 0 ? participantIds : ['__no_match__']);

      const privacyMap = new Map(
        (privacySettings || []).map(ps => [ps.user_id, ps])
      );

      // Apply privacy-aware name display to participants
      const enrichedParticipants = (participants || []).map(participant => {
        if (!participant.user) return participant;

        const participantId = participant.user_id;
        const isFriendWithParticipant = friendIds.includes(participantId) || participantId === user.id;

        const participantPrivacy = privacyMap.get(participantId);
        const showRealName = participantPrivacy?.show_real_name ?? true;
        // Nickname now comes from profiles.nickname (single source of truth)
        const privacyNickname = participant.user?.nickname;

        let displayName: string;
        if (isFriendWithParticipant || showRealName) {
          const firstName = participant.user?.first_name || '';
          const lastName = participant.user?.last_name || '';
          displayName = `${firstName} ${lastName}`.trim() || participant.user?.display_name || 'Unknown';
        } else {
          displayName = privacyNickname || participant.user?.display_name || 'Unknown';
        }

        return {
          ...participant,
          user: {
            ...participant.user,
            display_name: displayName
          }
        };
      });

      return {
        ...data,
        participants: enrichedParticipants
      };
    });
  }

  async getMessages(conversationId: string, limit: number = 50, offset: number = 0): Promise<ServiceResponse<PaginatedResponse<DirectMessage>>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Verify user is participant
      const { data: participant } = await this.supabase
        .from('conversation_participants')
        .select('id')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!participant) {
        throw new Error('Access denied to this conversation');
      }

      // Get friend IDs for privacy-aware name display
      const { data: friends } = await this.supabase
        .from('friend_relationships')
        .select('requester_id, addressee_id')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
        .eq('status', 'accepted');

      const friendIds = (friends || []).map(f =>
        f.requester_id === user.id ? f.addressee_id : f.requester_id
      );

      const { data, error, count } = await this.supabase
        .from('direct_messages')
        .select(`
          *,
          sender:profiles(
            id,
            first_name,
            last_name,
            display_name,
            avatar_url,
            nickname
          )
        `, { count: 'exact' })
        .eq('conversation_id', conversationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        throw error;
      }

      // Get unique sender IDs and fetch privacy settings (show_real_name toggle only)
      const senderIds = [...new Set((data || []).map(m => m.sender_id))];
      const { data: privacySettings } = await this.supabase
        .from('user_privacy_settings')
        .select('user_id, show_real_name')
        .in('user_id', senderIds.length > 0 ? senderIds : ['__no_match__']);

      const privacyMap = new Map(
        (privacySettings || []).map(ps => [ps.user_id, ps])
      );

      // Get message status and apply privacy-aware names
      const messages = await Promise.all((data || []).map(async (message) => {
        const { data: status } = await this.supabase
          .from('message_status')
          .select('user_id, status, timestamp')
          .eq('message_id', message.id);

        // Apply privacy-aware name display
        let senderDisplayName = message.sender?.display_name || 'Unknown';
        if (message.sender) {
          const senderId = message.sender_id;
          const isFriendWithSender = friendIds.includes(senderId) || senderId === user.id;

          const senderPrivacy = privacyMap.get(senderId);
          const showRealName = senderPrivacy?.show_real_name ?? true;
          // Nickname now comes from profiles.nickname (single source of truth)
          const privacyNickname = message.sender?.nickname;

          if (isFriendWithSender || showRealName) {
            const firstName = message.sender?.first_name || '';
            const lastName = message.sender?.last_name || '';
            senderDisplayName = `${firstName} ${lastName}`.trim() || message.sender?.display_name || 'Unknown';
          } else {
            senderDisplayName = privacyNickname || message.sender?.display_name || 'Unknown';
          }
        }

        return {
          ...message,
          sender: message.sender ? {
            ...message.sender,
            display_name: senderDisplayName
          } : message.sender,
          status: status || []
        };
      }));

      return {
        data: messages,
        total: count || 0,
        page: Math.floor(offset / limit) + 1,
        limit,
        hasNextPage: (offset + limit) < (count || 0)
      };
    });
  }

  async sendMessage(data: SendMessageRequest): Promise<ServiceResponse<DirectMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Verify user is participant
      const { data: participant } = await this.supabase
        .from('conversation_participants')
        .select('id')
        .eq('conversation_id', data.conversation_id)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!participant) {
        throw new Error('Access denied to this conversation');
      }

      // Create message
      const { data: message, error } = await this.supabase
        .from('direct_messages')
        .insert({
          conversation_id: data.conversation_id,
          sender_id: user.id,
          content: data.content,
          message_type: data.message_type || 'text',
          message_data: data.message_data || {},
          reply_to_id: data.reply_to_id
        })
        .select(`
          *,
          sender:profiles(
            id,
            display_name,
            avatar_url
          )
        `)
        .single();

      if (error) {
        throw error;
      }

      // Note: message_status table has FK constraint to club_channel_messages only,
      // so we skip status creation for direct messages.
      // Direct message read tracking uses conversation_participants.last_read_at instead.

      // Get participants for Member 360 tracking
      const { data: participants } = await this.supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', data.conversation_id)
        .eq('is_active', true);

      // Track Member 360 communication activity (fail-safe design)
      try {
        const { Member360IntegrationService } = await import('./member360/member360-integration.service');
        const member360Service = new Member360IntegrationService();
        
        await member360Service.processCommunicationActivity({
          activityType: 'message_sent',
          userId: user.id,
          conversationId: data.conversation_id,
          messageType: data.message_type || 'text',
          messageLength: data.content?.length || 0,
          hasAttachment: (data.message_data && Object.keys(data.message_data).length > 0),
          participantCount: participants?.length || 0,
          messageId: message.id
        });
        
        console.log(`✅ Member 360 communication activity tracked for message ${message.id}`);
      } catch (member360Error) {
        // Member 360 failures don't break messaging functionality
        console.warn('⚠️ Member 360 communication activity tracking failed (non-blocking):', member360Error);
      }

      // Track contact history for staff-member conversations (fail-safe design)
      try {
        console.log('🔍 [MESSAGING-SERVICE] Attempting contact history logging for message:', message.id);

        // Import contact history logic from UnifiedMessagingService
        const { UnifiedMessagingService } = await import('./unified-messaging.service');
        const unifiedService = UnifiedMessagingService.getInstance();

        // Use the same logic we built for UnifiedMessagingService
        await unifiedService.logToContactHistoryIfRelevant(message, data.conversation_id);

        console.log('✅ [MESSAGING-SERVICE] Contact history logged for message:', message.id);
      } catch (contactHistoryError) {
        // Contact history failures don't break messaging functionality
        console.warn('⚠️ [MESSAGING-SERVICE] Contact history tracking failed (non-blocking):', contactHistoryError);
      }

      return message;
    });
  }

  async markMessageAsRead(messageId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await this.supabase
        .from('message_status')
        .update({ status: 'read', timestamp: new Date().toISOString() })
        .eq('message_id', messageId)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      return undefined;
    });
  }

  async markConversationAsRead(conversationId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Mark all messages as read (like notifications: simple read boolean)
      const { error } = await this.supabase
        .from('direct_messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .eq('read', false);

      // Silently ignore if 'read' column doesn't exist (migration not run)
      if (error && !error.message?.includes('read')) {
        console.warn('⚠️ Failed to mark conversation as read:', error);
      }

      return undefined;
    });
  }

  async deleteMessage(messageId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await this.supabase
        .from('direct_messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', messageId)
        .eq('sender_id', user.id);

      if (error) {
        throw error;
      }

      return undefined;
    });
  }

  async editMessage(messageId: string, newContent: string): Promise<ServiceResponse<DirectMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: message, error } = await this.supabase
        .from('direct_messages')
        .update({ 
          content: newContent,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id)
        .select(`
          *,
          sender:profiles(
            id,
            display_name,
            avatar_url
          )
        `)
        .single();

      if (error) {
        throw error;
      }

      return message;
    });
  }

  async searchUsers(query: string = '', limit: number = 10): Promise<ServiceResponse<UserProfile[]>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      let queryBuilder = this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url, handicap')
        .neq('id', user.id) // Exclude current user
        .limit(limit);

      if (query.trim()) {
        queryBuilder = queryBuilder.or(`display_name.ilike.%${query}%,email.ilike.%${query}%`);
      }

      const { data, error } = await queryBuilder;

      if (error) {
        throw error;
      }

      return data || [];
    });
  }

  async getUnreadMessageCount(): Promise<ServiceResponse<number>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get user's conversation IDs
      const { data: userConversations, error: convError } = await this.supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (convError) {
        console.error('Error fetching conversations for unread count:', convError);
        return 0;
      }

      if (!userConversations || userConversations.length === 0) {
        return 0;
      }

      const conversationIds = userConversations.map(c => c.conversation_id).filter(Boolean);
      if (conversationIds.length === 0) return 0;

      // Count unread messages (read = false, not sent by current user)
      // Like notifications: simple read boolean column
      const { count, error } = await this.supabase
        .from('direct_messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', conversationIds)
        .neq('sender_id', user.id)
        .eq('read', false);

      // If 'read' column doesn't exist yet, return 0 (migration not run)
      if (error?.message?.includes('read')) {
        return 0;
      }

      if (error) {
        console.error('Error counting unread messages:', error);
        return 0;
      }

      return count || 0;
    });
  }

  async getUnreadCoachMessageCount(): Promise<ServiceResponse<number>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get user's conversation IDs
      const { data: userConversations, error: convError } = await this.supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (convError) {
        console.error('Error fetching conversations for unread coach messages:', convError);
        return 0;
      }

      if (!userConversations || userConversations.length === 0) {
        return 0;
      }

      const conversationIds = userConversations.map(c => c.conversation_id).filter(Boolean);
      if (conversationIds.length === 0) return 0;

      // Get unread messages from coaches (sender_id must be a coach)
      const { data: unreadMessages, error: messagesError } = await this.supabase
        .from('direct_messages')
        .select('sender_id')
        .in('conversation_id', conversationIds)
        .neq('sender_id', user.id)
        .eq('read', false);

      if (messagesError) {
        console.error('Error fetching unread messages:', messagesError);
        return 0;
      }

      if (!unreadMessages || unreadMessages.length === 0) {
        return 0;
      }

      // Get unique sender IDs
      const senderIds = Array.from(new Set(unreadMessages.map(m => m.sender_id)));
      if (senderIds.length === 0) return 0;

      // Check which senders are coaches
      const { data: coaches, error: coachError } = await this.supabase
        .from('coach_profiles')
        .select('user_id')
        .in('user_id', senderIds);

      if (coachError) {
        console.error('Error fetching coach profiles:', coachError);
        return 0;
      }

      if (!coaches || coaches.length === 0) {
        return 0;
      }

      const coachUserIds = coaches.map(c => c.user_id);

      // Count unread messages from coaches
      const count = unreadMessages.filter(m => coachUserIds.includes(m.sender_id)).length;

      return count;
    });
  }

  // Real-time subscriptions
  private subscriptions: Map<string, any> = new Map();

  subscribeToConversation(conversationId: string, callback: (message: DirectMessage) => void) {
    const channelName = `conversation-${conversationId}`;
    
    // Unsubscribe from existing subscription if any
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromConversation(conversationId);
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `conversation_id=eq.${conversationId}`
      }, (payload) => {
        // Fetch full message data with sender info
        this.supabase
          .from('direct_messages')
          .select(`
            *,
            sender:profiles(
              id,
              display_name,
              avatar_url
            )
          `)
          .eq('id', payload.new.id)
          .single()
          .then(({ data }) => {
            if (data) {
              callback(data);
            }
          });
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  subscribeToConversations(callback: (conversation: Conversation) => void) {
    const channelName = 'conversations';
    
    // Unsubscribe from existing subscription if any
    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromConversations();
    }
    
    const channel = this.supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations'
      }, (payload) => {
        // Only handle INSERT/UPDATE — DELETE has no payload.new.id and the user has no access anyway
        if (payload.eventType === 'DELETE' || !payload.new?.id) return;
        this.getConversation(payload.new.id).then(response => {
          if (response.success && response.data) {
            callback(response.data);
          }
        });
      })
      .subscribe();
    
    this.subscriptions.set(channelName, channel);
    return channel;
  }

  unsubscribeFromConversation(conversationId: string) {
    const channelName = `conversation-${conversationId}`;
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      try {
        channel.unsubscribe();
        this.supabase.removeChannel(channel); // FIX: Add removeChannel for complete cleanup
        this.subscriptions.delete(channelName);
      } catch (error) {
        console.error('Error unsubscribing from conversation:', error);
      }
    }
  }

  unsubscribeFromConversations() {
    const channelName = 'conversations';
    const channel = this.subscriptions.get(channelName);
    
    if (channel) {
      try {
        channel.unsubscribe();
        this.supabase.removeChannel(channel); // FIX: Add removeChannel for complete cleanup
        this.subscriptions.delete(channelName);
      } catch (error) {
        console.error('Error unsubscribing from conversations:', error);
      }
    }
  }
  
  // FIX: Add global cleanup method for logout
  destroy() {
    console.log('🧹 [MessagingService] Cleaning up all subscriptions');
    this.subscriptions.forEach((channel, name) => {
      try {
        channel.unsubscribe();
        this.supabase.removeChannel(channel);
      } catch (error) {
        console.error(`Error cleaning up channel ${name}:`, error);
      }
    });
    this.subscriptions.clear();
  }

  // ============================================
  // Direct Message Reactions
  // ============================================

  async addDirectMessageReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('direct_message_reactions')
        .insert({
          message_id: messageId,
          user_id: user.id,
          emoji
        });

      if (error) {
        // Handle duplicate reaction gracefully
        if (error.code === '23505') {
          // Unique constraint violation - reaction already exists
          return;
        }
        throw error;
      }
    });
  }

  async removeDirectMessageReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('direct_message_reactions')
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .eq('emoji', emoji);

      if (error) throw error;
    });
  }

  async getDirectMessageReactions(messageId: string): Promise<ServiceResponse<Array<{ emoji: string; user_id: string; created_at: string }>>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('direct_message_reactions')
        .select('emoji, user_id, created_at')
        .eq('message_id', messageId);

      if (error) throw error;
      return data || [];
    });
  }

  async getDirectMessageReactionCounts(messageId: string): Promise<ServiceResponse<{ reactionCounts: Record<string, number>; userReactions: Record<string, boolean> }>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await this.supabase
        .from('direct_message_reactions')
        .select('emoji, user_id')
        .eq('message_id', messageId);

      if (error) throw error;

      const reactionCounts: Record<string, number> = {};
      const userReactions: Record<string, boolean> = {};

      (data || []).forEach(reaction => {
        reactionCounts[reaction.emoji] = (reactionCounts[reaction.emoji] || 0) + 1;
        if (reaction.user_id === user.id) {
          userReactions[reaction.emoji] = true;
        }
      });

      return { reactionCounts, userReactions };
    });
  }
}

export default MessagingService.getInstance();